import { useState, useCallback, useMemo, type ReactNode } from 'react'
import { Icons } from '@journ/ui/icons'
import { SubmitButton } from '@journ/ui/submit-button'
import { AnimatePresence, motion } from 'framer-motion'
import { useIntlayer } from 'react-intlayer'
import { SetNameStep } from './set-name-step'
import { CreateTeamStep } from './create-team-step'

type StepConfig = {
	key: string
	content: ReactNode
}

type Props = {
	onComplete: () => void
}

const ProgressBar = ({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) => {
	if (currentStep >= totalSteps) return null

	return (
		<div className='flex justify-center'>
			<motion.div
				layoutId='progress-bar-container'
				transition={{ duration: 0.4, ease: 'easeOut' }}
				className='w-32 h-1 bg-border overflow-hidden'
			>
				<motion.div
					layoutId='progress-bar-fill'
					initial={{ width: 0 }}
					animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
					transition={{ duration: 0.4, ease: 'easeOut' }}
					className='h-full bg-primary'
				/>
			</motion.div>
		</div>
	)
}

const OnboardingPage = ({ onComplete }: Props) => {
	const [step, setStep] = useState(1)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const content = useIntlayer('onboarding-page')

	const handleLoadingChange = useCallback((loading: boolean) => setIsSubmitting(loading), [])

	const steps: StepConfig[] = useMemo(
		() => [
			{
				key: 'set-name',
				content: <SetNameStep onComplete={() => setStep(2)} onLoadingChange={handleLoadingChange} />,
			},
			{
				key: 'create-team',
				content: <CreateTeamStep onComplete={onComplete} onLoadingChange={handleLoadingChange} />,
			},
		],
		[onComplete, handleLoadingChange],
	)

	const totalSteps = steps.length
	const currentStep = steps[step - 1]

	if (!currentStep) return null

	return (
		<div className='h-screen overflow-hidden flex relative'>
			<nav className='fixed top-0 left-0 right-0 z-50 w-full pointer-events-none'>
				<div className='relative py-3 xl:py-4 px-4 xl:px-6 2xl:px-8 flex items-center justify-between'>
					<div className='w-6 h-6'>
						<Icons.LogoSmall className='w-full h-full text-foreground' />
					</div>
				</div>
			</nav>

			<div className='w-full flex flex-col items-center p-8 lg:p-12 pt-10 dark:bg-0c0c0c text-foreground'>
				<div className='w-full max-w-md flex flex-col h-full relative'>
					<div className='relative h-6 mb-2 flex items-center justify-center'>
						<ProgressBar currentStep={step} totalSteps={totalSteps} />
					</div>

					<div className='flex-1 flex flex-col justify-center pt-20 min-h-0 overflow-y-auto scrollbar-hide'>
						<motion.div layout transition={{ layout: { duration: 0.3, ease: 'easeInOut' } }}>
							<AnimatePresence mode='wait' initial={false}>
								<motion.div
									key={currentStep.key}
									layout
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ opacity: { duration: 0.2 }, layout: { duration: 0.3, ease: 'easeInOut' } }}
								>
									{currentStep.content}
								</motion.div>
							</AnimatePresence>
						</motion.div>
					</div>

					<div className='mt-auto pt-8'>
						<div className='flex justify-end'>
							<SubmitButton
								type='submit'
								form={currentStep.key + '-form'}
								isSubmitting={isSubmitting}
								className='px-4 py-2 bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors'
							>
								{content.continue}
							</SubmitButton>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export { OnboardingPage }
